import { useEffect, useState } from "react";
import { Plus, PencilLine, Power } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { MediaPickerDialog } from "../../components/shared/MediaPickerDialog";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", category: "", description: "", logo: "", link: "", location: "", order: 0, featured: false };

export default function PartnersAdminPage() {
  const [partners, setPartners] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = () => api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => setPartners([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, category: p.category || "", description: p.description || "", logo: p.logo || "", link: p.link || "", location: p.location || "", order: p.order || 0, featured: p.featured || false }); setOpen(true); };

  const submit = async () => {
    const payload = { ...form, category: form.category || null, description: form.description || null, logo: form.logo || null, link: form.link || null, location: form.location || null, order: Number(form.order) || 0 };
    try {
      if (editing) { await api.patch(`/admin/partners/${editing.id}`, payload); toast.success("Partenaire mis à jour"); }
      else { await api.post("/admin/partners", payload); toast.success("Partenaire créé"); }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (p) => {
    try { await api.post(`/admin/partners/${p.id}/toggle`); toast.success(p.active ? "Partenaire désactivé — retiré du site public" : "Partenaire activé — visible sur le site public"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!partners) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="partners-loading">Chargement…</div>;

  return (
    <div data-testid="admin-partners-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline-ae">B19 — Partenaires</p>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Partenaires</h1>
          <p className="mt-1 text-sm text-muted-foreground">Publiés uniquement sur décision d'Art Event — aucun faux partenaire définitif.</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid="partner-create">
          <Plus className="h-4 w-4" /> Nouveau partenaire
        </button>
      </div>

      {partners.length === 0 ? (
        <div className="mt-6"><EmptyState title="Aucun partenaire">Les vrais partenaires seront publiés ici.</EmptyState></div>
      ) : (
        <div className="card-ae mt-6 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" data-testid="partners-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3">Partenaire</th>
                <th className="px-5 py-3">Catégorie</th>
                <th className="px-5 py-3 text-center">Ordre</th>
                <th className="px-5 py-3 text-center">Mis en avant</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {partners.map((p) => (
                <tr key={p.id} className="transition-colors hover:bg-cream/60" data-testid={`partner-row-${p.id}`}>
                  <td className="px-5 py-3.5 font-semibold text-deepgreen">{p.name}</td>
                  <td className="px-5 py-3.5 text-muted-foreground">{p.category || "—"}</td>
                  <td className="px-5 py-3.5 text-center">{p.order}</td>
                  <td className="px-5 py-3.5 text-center">{p.featured ? "★" : "—"}</td>
                  <td className="px-5 py-3.5"><StatusBadge status={p.active ? "Actif" : "Archivé"} /></td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button type="button" onClick={() => openEdit(p)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`partner-edit-${p.id}`}><PencilLine className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => toggle(p)} className="rounded-full border border-border px-3 py-1.5 text-[11px] font-bold text-deepgreen hover:bg-sage-light" data-testid={`partner-toggle-${p.id}`}>
                        <Power className="mr-1 inline h-3.5 w-3.5" />{p.active ? "Désactiver" : "Activer"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream" data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-deepgreen">{editing ? "Modifier le partenaire" : "Nouveau partenaire"}</DialogTitle>
            <DialogDescription>Logo depuis la Médiathèque centrale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="label-ae" htmlFor="p-name">Nom</label><input id="p-name" className="input-ae" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name" /></div>
              <div><label className="label-ae" htmlFor="p-category">Catégorie</label><input id="p-category" className="input-ae" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="partner-category" /></div>
              <div className="sm:col-span-2"><label className="label-ae" htmlFor="p-desc">Description</label><textarea id="p-desc" className="input-ae min-h-20" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="partner-description" /></div>
              <div>
                <label className="label-ae" htmlFor="p-logo">Logo (URL médiathèque)</label>
                <input id="p-logo" className="input-ae" value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} data-testid="partner-logo" />
                <button type="button" onClick={() => setPickerOpen(true)} className="btn-secondary-ae mt-2 !px-4 !py-1.5 !text-xs" data-testid="partner-pick-media">Choisir depuis la Médiathèque</button>
                {form.logo && <img src={form.logo} alt="Aperçu du logo choisi" className="mt-2 h-12 w-auto rounded-lg border border-border bg-white object-contain p-1" data-testid="partner-logo-preview" />}
              </div>
              <div><label className="label-ae" htmlFor="p-link">Lien</label><input id="p-link" className="input-ae" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="partner-link" /></div>
              <div><label className="label-ae" htmlFor="p-location">Lieu / Ville</label><input id="p-location" className="input-ae" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="partner-location" /></div>
              <div><label className="label-ae" htmlFor="p-order">Ordre d'affichage</label><input id="p-order" type="number" className="input-ae" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="partner-order" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold text-deepgreen">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#2C4C3B]" data-testid="partner-featured" />
              Mettre en avant
            </label>
            <button type="button" onClick={submit} disabled={form.name.length < 2} className="btn-primary-ae modal-sticky-btn w-full disabled:opacity-50" data-testid="partner-submit">
              {editing ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} multiple onSelect={(imgs) => setForm({ ...form, logo: imgs.at(-1).url })} />
    </div>
  );
}
